import React, { useState, useEffect, useRef, useCallback } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import FieldHelpTooltip from "../Help/FieldHelpTooltip";
import { HELP_CONTENT } from "../Help/HelpContent";
import PhoneInput from "react-phone-input-2";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import { toast } from "react-toastify";
import { FiX, FiShoppingCart } from "react-icons/fi";
import * as apicall from "../Apicall";
import { google_places_key } from "../../constants/Constants";
import { DAYS, deriveScheduleMessage } from "../../utils/scheduleDateMath";
import PaymentMethodSelector from "../shared/PaymentMethodSelector";

const FIXED_TYPES = ["Weekly", "Monthly", "Quarterly", "Half Yearly"];
const TIME_SLOTS = Array.from({ length: 33 }, (_, i) => {
  const totalMins = 360 + i * 30; // 06:00 → 22:00
  const hours = Math.floor(totalMins / 60);
  const minutes = totalMins % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
});

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const PurchaseSlider = ({ plan, currency, onClose, industry }) => {
  const vendor_id = JSON.parse(localStorage.getItem("userId"));
  const isClinicService = Number(industry) === 1;
  const isSportsService = Number(industry) === 7;

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [locationError, setLocationError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [userLookupLoading, setUserLookupLoading] = useState(false);
  const autocompleteRef = useRef(null);
  const formikRef = useRef(null);

  // ── Payment step (shown inside this same slider — no separate page) ──────
  const isRecurringPlan = plan?.paymentType === "Recurring";
  const [step, setStep] = useState("details"); // "details" | "payment"
  // No default method — the vendor must explicitly pick Cash or Pay Online
  // before Confirm Purchase becomes clickable (see disabled check below).
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentStatusOptions, setPaymentStatusOptions] = useState([]);
  const [cashPaymentStatus, setCashPaymentStatus] = useState(null);
  const [proratePreview, setProratePreview] = useState(null);
  const [proratePreviewLoading, setProratePreviewLoading] = useState(false);

  // Plan Information state (only used when plan.type === "Open")
  const [planInfo, setPlanInfo] = useState({ scheduleType: "Fixed", frequency: "Monthly", startDate: "", days: [], timeSlot: "", crewId: null });
  const [planInfoErrors, setPlanInfoErrors] = useState({});
  const [crewList, setCrewList] = useState([]);
  const [crewLoading, setCrewLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: google_places_key,
    libraries: ["places"],
  });

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await apicall.get("country");
        if (response.success) {
          const data = Array.isArray(response.data)
            ? response.data
            : response.data?.countries || [];
          setCountries(data);
        }
      } catch {
        // countries dropdown is cosmetic — don't block the form
      }
    };

    const loadStates = async () => {
      try {
        const countryId = currency === "INR" ? 2 : 1;
        const response = await apicall.get(`state/${countryId}`);
        if (response.data && Array.isArray(response.data)) {
          setStates(response.data.filter(s => s && s.name));
        }
      } catch {
        setStates([]);
      }
    };

    loadCountries();
    loadStates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (plan?.type !== "Open") return;

    const loadCrew = async () => {
      try {
        setCrewLoading(true);
        const serviceFilter = plan?.serviceId ? `&services=${plan.serviceId}` : "";
        const res = await apicall.get(`get_ezp_service_providers?limit=100&page=1&skill=PROFESSIONAL${serviceFilter}`);
        if (res?.status === 200 && Array.isArray(res.data)) {
          setCrewList(
            res.data.map(c => {
              const name = c.provider_name
                ? c.provider_name.replace(",", " ").trim()
                : `${c.first_name || ""} ${c.last_name || ""}`.trim();
              return { label: name || "Unknown", value: c.service_provider };
            })
          );
        } else {
          setCrewList([]);
        }
      } catch {
        setCrewList([]);
      } finally {
        setCrewLoading(false);
      }
    };

    loadCrew();
  }, [plan]);

  // Cash payment status options — same source/default as booking Checkout.jsx
  useEffect(() => {
    (async () => {
      try {
        const res = await apicall.get("get_ezp_payment_status");
        if (res?.success && res.data?.length) {
          setPaymentStatusOptions(res.data);
          const pending = res.data.find(p => (p.name || "").toLowerCase() === "pending");
          setCashPaymentStatus(pending ? pending.id : res.data[0].id);
        }
      } catch {
        // cosmetic dropdown only — don't block the form
      }
    })();
  }, []);

  // Pro-rate preview — fetched once, when entering the payment step for a
  // Monthly Recurring plan. Backend is the source of truth for the formula;
  // this only renders whatever it returns.
  const loadProratePreview = useCallback(async () => {
    if (!isRecurringPlan || !plan?.id) return;
    try {
      setProratePreviewLoading(true);
      const res = await apicall.get(`subscription-plans/${plan.id}/prorate-preview`);
      setProratePreview(res?.data || null);
    } catch (e) {
      toast.error(e?.message || "Failed to calculate pro-rated price");
    } finally {
      setProratePreviewLoading(false);
    }
  }, [isRecurringPlan, plan?.id]);

  // ---------- Phone-number user lookup (same logic as New Booking page) ----------
  const getUserDetails = async (ph_number, setFieldValue) => {
    try {
      setUserLookupLoading(true);
      const response = await apicall.get(
        `ezp_get_users_details/${ph_number}/${vendor_id}`
      );

      if (response.status === 200 && response.data.length > 0) {
        const user = response.data[0];
        const fullName = `${user.first_name} ${user.middle_name || ""} ${
          user.last_name || ""
        }`.trim();

        setFieldValue("userName", fullName);
        setFieldValue("email", user?.email || "");
        setFieldValue("house_no", user?.house_no || "");
        setFieldValue("area", user?.area || "");
        setFieldValue("landmark", user?.landmark || "");
        setFieldValue("state", user?.state || "");
        setFieldValue("location", user?.location || "");
        setFieldValue("lat", user?.lat || "");
        setFieldValue("lng", user?.lng || "");
        setFieldValue("location_id", user?.location_id || "");
      } else {
        setFieldValue("userName", "");
        setFieldValue("email", "");
        setFieldValue("house_no", "");
        setFieldValue("area", "");
        setFieldValue("landmark", "");
        setFieldValue("state", "");
        setFieldValue("location", "");
        setFieldValue("lat", "");
        setFieldValue("lng", "");
      }
    } catch {
      // silently fail — user may just be new
    } finally {
      setUserLookupLoading(false);
    }
  };

  const debouncedCheckUser = useCallback(
    debounce((ph_number, setFieldValue) => {
      getUserDetails(ph_number, setFieldValue);
    }, 500),
    []
  );
  // -----------------------------------------------------------------------------

  // ---------- Google Maps address autocomplete (same logic as New Booking page) ----------
  const onLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = (setFieldValue) => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.address_components) return;

    let street = place.formatted_address || "";
    let area = "";
    let state = "";
    let country = "";
    let lat = place.geometry?.location?.lat();
    let lng = place.geometry?.location?.lng();

    place.address_components.forEach((comp) => {
      const types = comp.types;
      if (types.includes("sublocality") || types.includes("locality")) {
        area = comp.long_name;
      }
      if (types.includes("administrative_area_level_1")) {
        state = comp.long_name;
      }
      if (types.includes("country")) {
        country = comp.short_name?.toLowerCase();
      }
    });

    const expectedCountry = currency === "INR" ? "in" : "ae";
    if (!country || country !== expectedCountry) {
      setLocationError(
        currency === "INR"
          ? "Please select a location within India"
          : "Please select a location within UAE"
      );
      setFieldValue("location", "");
      setFieldValue("area", "");
      setFieldValue("state", "");
      setFieldValue("lat", "");
      setFieldValue("lng", "");
      return;
    }

    setLocationError("");
    setFieldValue("location", street);
    setFieldValue("area", area);
    setFieldValue("state", state);
    setFieldValue("lat", lat);
    setFieldValue("lng", lng);
  };
  // ---------------------------------------------------------------------------------

  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .required("Mobile Number is required")
      .test("country-specific-length", function (value) {
        if (!value) return true;
        const cleaned = value.replace(/\D/g, "");
        if (cleaned.startsWith("91")) {
          return (
            cleaned.length >= 12 ||
            this.createError({
              message: "Please enter a valid Indian mobile number (min 10 digits)",
            })
          );
        }
        if (cleaned.startsWith("971")) {
          return (
            cleaned.length >= 12 ||
            this.createError({
              message: "Please enter a valid UAE mobile number (min 9 digits)",
            })
          );
        }
        return (
          cleaned.length >= 10 ||
          this.createError({
            message: "Please enter a valid mobile number (min 10 digits)",
          })
        );
      }),
    userName: Yup.string()
      .required(isClinicService ? "Patient name is required" : isSportsService ? "Player name is required" : "Customer name is required")
      .min(2, isClinicService ? "Patient name must be at least 2 characters" : isSportsService ? "Player name must be at least 2 characters" : "Customer name must be at least 2 characters")
      .max(20, isClinicService ? "Patient name must not exceed 20 characters" : isSportsService ? "Player name must not exceed 20 characters" : "Customer name must not exceed 20 characters"),
    email: Yup.string()
      .nullable()
      .test("valid-email", "Enter valid email (.com, .co.in only)", (value) => {
        if (!value) return true;
        return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|co\.in)$/.test(value);
      }),
    house_no: Yup.string()
      .required("Street / House no. is required")
      .max(100, "Street address must not exceed 100 characters"),
    area: Yup.string().required("Area is required"),
    location: Yup.string().required("Address is required"),
    state: Yup.string().required(
      currency === "INR" ? "State is required" : "Emirate is required"
    ),
    country: Yup.string().required("Country is required"),
  });

  const initialValues = {
    phoneNumber: "",
    userName: "",
    email: "",
    house_no: "",
    area: "",
    location: "",
    state: "",
    landmark: "",
    country: currency === "INR" ? "2" : "1",
    lat: "",
    lng: "",
  };

  // Advances from the "details" step to the "payment" step within this same
  // slider (no navigation to a separate page/route). Runs the same
  // validation handleSubmit used to run before the payment step existed.
  const handleContinue = async (validateForm, setTouched, values) => {
    if (plan?.type === "Open") {
      const planErrs = {};
      if (planInfo.scheduleType === "Fixed") {
        if (!planInfo.startDate) planErrs.startDate = "Start date is required";
        if (planInfo.days.length === 0) planErrs.days = "Select at least one day";
        if (!planInfo.timeSlot) planErrs.timeSlot = "Preferred time slot is required";
      }
      if (Object.keys(planErrs).length > 0) {
        setPlanInfoErrors(planErrs);
        return;
      }
    }

    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      setTouched(Object.keys(values).reduce((acc, k) => ({ ...acc, [k]: true }), {}), false);
      return;
    }

    setStep("payment");
    if (isRecurringPlan) loadProratePreview();
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      const payload = {
        plan_id: plan?.id,
        vendor_id: vendor_id,
        mobileNumber: values.phoneNumber,
        userName: values.userName,
        email: values.email || "",
        house_no: values.house_no,
        area: values.area,
        location: values.location,
        state: values.state,
        landmark: values.landmark || "",
        lat: values.lat || "",
        lng: values.lng || "",
        payment_method: selectedPayment,
        payment_status_id: selectedPayment === "cash" ? cashPaymentStatus : undefined,
      };

      if (plan?.type === "Open") {
        payload.schedule_type = planInfo.scheduleType;
        if (planInfo.scheduleType === "Fixed") {
          payload.schedule_frequency = planInfo.frequency;
          payload.schedule_start_date = planInfo.startDate || null;
          payload.schedule_days = JSON.stringify(planInfo.days);
          payload.schedule_time_slot = planInfo.timeSlot;
          payload.schedule_provider_id = planInfo.crewId ? parseInt(planInfo.crewId, 10) : null;
        }
      }

      const res = await apicall.post(payload, "purchase-subscription");

      const amount = res?.data?.purchase_price;
      toast.success(
        amount != null
          ? `Subscription purchased successfully! Charged ${currency} ${Number(amount).toFixed(2)}.`
          : "Subscription purchased successfully!"
      );
      onClose();
    } catch (e) {
      toast.error(e?.message || "Failed to purchase subscription");
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = {
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "6px",
  };

  const sectionHeadingStyle = {
    fontSize: "14px",
    fontWeight: "700",
    color: "#C0335A",
    borderBottom: "2px solid #FDE8EF",
    paddingBottom: "8px",
    marginBottom: "16px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.35)",
          zIndex: 1040,
        }}
      />

      {/* Slider panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "min(680px, 100vw)",
          height: "100vh",
          background: "#fff",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
          animation: "slideInRight 0.28s ease",
        }}
      >
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); }
            to   { transform: translateX(0); }
          }
          .purchase-input:focus {
            border-color: #C0335A !important;
            box-shadow: 0 0 0 3px rgba(192,51,90,0.12) !important;
          }
          .pac-container {
            z-index: 10000 !important;
          }
        `}</style>

        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <FiShoppingCart size={20} color="#C0335A" />
            <h5 style={{ margin: 0, fontWeight: "700", color: "#111827", fontSize: "17px" }}>
              Purchase Plan
            </h5>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#6B7280",
              padding: "4px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Plan summary banner */}
        <div
          style={{
            padding: "14px 24px",
            background: "#FDE8EF",
            borderBottom: "1px solid #F9A8C0",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            <div>
              <div style={{ fontSize: "13px", color: "#9B1C3E", fontWeight: "600", marginBottom: "2px" }}>
                Selected Plan
              </div>
              <div style={{ fontSize: "15px", fontWeight: "700", color: "#7B1031" }}>
                {plan?.planName}
              </div>
              <div style={{ fontSize: "12px", color: "#9B1C3E", marginTop: "2px" }}>
                {plan?.serviceType} &bull; {plan?.type}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              {isRecurringPlan ? (
                <>
                  <div style={{ fontSize: "20px", fontWeight: "800", color: "#C0335A" }}>
                    {currency} {Number(plan?.recurringAmount || 0).toFixed(2)}
                    <span style={{ fontSize: "12px", fontWeight: "600" }}>/mo</span>
                  </div>
                  <div style={{ fontSize: "11px", color: "#9B1C3E" }}>
                    Due on the {plan?.recurringDueDay || "-"} of every month
                  </div>
                </>
              ) : (
                <div style={{ fontSize: "20px", fontWeight: "800", color: "#C0335A" }}>
                  {currency} {Number(plan?.pricing || 0).toFixed(2)}
                  <span style={{ fontSize: "12px", fontWeight: "600" }}> one-time</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Scrollable form body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit: formSubmit,
              setFieldValue,
              setFieldTouched,
              validateForm,
              setTouched,
            }) => (
              <Form onSubmit={formSubmit}>
                {step === "details" && (
                <>
                {/* ── Customer Information ── */}
                <div style={{ marginBottom: "24px" }}>
                  <h6 style={sectionHeadingStyle}>{isClinicService ? "Patient Information" : isSportsService ? "Player Information" : "Customer Information"}</h6>

                  <Row className="g-3">
                    {/* Mobile Number */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>
                          Mobile Number <span style={{ color: "red" }}>*</span>
                          <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.phoneNumber} />
                        </Form.Label>
                        <div style={{ position: "relative" }}>
                          <PhoneInput
                            country={currency === "INR" ? "in" : "ae"}
                            onlyCountries={["ae", "in"]}
                            value={values.phoneNumber}
                            onChange={(value) => {
                              const cleaned = value.replace(/\D/g, "");
                              setFieldValue("phoneNumber", cleaned);
                              setFieldTouched("phoneNumber", true, false);
                              if (cleaned.length >= 12) {
                                debouncedCheckUser(cleaned, setFieldValue);
                              }
                            }}
                            inputStyle={{
                              width: "100%",
                              height: "40px",
                              backgroundColor: "white",
                              borderColor:
                                touched.phoneNumber && errors.phoneNumber
                                  ? "#dc3545"
                                  : "#E9ECEF",
                            }}
                            inputProps={{ name: "phoneNumber" }}
                          />
                          {userLookupLoading && (
                            <span
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            >
                              <Spinner animation="border" size="sm" variant="danger" />
                            </span>
                          )}
                        </div>
                        {touched.phoneNumber && errors.phoneNumber && (
                          <div
                            className="invalid-feedback d-block"
                            style={{ fontSize: "12px" }}
                          >
                            {errors.phoneNumber}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Customer Name */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>
                          {isClinicService ? "Patient Name" : isSportsService ? "Player Name" : "Customer Name"} <span style={{ color: "red" }}>*</span>
                          <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.customerName} />
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="userName"
                          placeholder={isClinicService ? "Enter patient name..." : isSportsService ? "Enter player name..." : "Enter customer name..."}
                          value={values.userName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.userName && errors.userName}
                          className="purchase-input"
                          style={{ height: "40px" }}
                        />
                        {touched.userName && errors.userName && (
                          <Form.Control.Feedback type="invalid">
                            {errors.userName}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Email */}
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>Email Address <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.email} /></Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder="Enter email address"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.email && errors.email}
                          className="purchase-input"
                          style={{ height: "40px" }}
                        />
                        {touched.email && errors.email && (
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* ── Plan Information (Open plans only) ── */}
                {plan?.type === "Open" && (
                  <div style={{ marginBottom: "24px" }}>
                    <h6 style={sectionHeadingStyle}>Plan Information</h6>

                    {/* Schedule Type toggle */}
                    <div style={{ marginBottom: "16px" }}>
                      <Form.Label style={labelStyle}>
                        Type <span style={{ color: "red" }}>*</span>
                        <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.scheduleType} />
                      </Form.Label>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {["Fixed", "Flexi"].map(t => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => {
                              setPlanInfo(p => ({ ...p, scheduleType: t }));
                              setPlanInfoErrors({});
                            }}
                            style={{
                              flex: 1,
                              padding: "9px 12px",
                              border: `1px solid ${planInfo.scheduleType === t ? "#C0335A" : "#E0E0E0"}`,
                              borderRadius: "8px",
                              background: planInfo.scheduleType === t ? "#FDE8EF" : "#fff",
                              color: planInfo.scheduleType === t ? "#C0335A" : "#374151",
                              fontWeight: planInfo.scheduleType === t ? "600" : "400",
                              fontSize: "14px",
                              cursor: "pointer",
                              transition: "all 0.15s",
                            }}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>

                    {planInfo.scheduleType === "Fixed" && (
                      <>
                        {/* Frequency */}
                        <div style={{ marginBottom: "16px" }}>
                          <Form.Label style={labelStyle}>
                            Frequency <span style={{ color: "red" }}>*</span>
                            <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.frequency} />
                          </Form.Label>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                            {FIXED_TYPES.map(ft => (
                              <button
                                key={ft}
                                type="button"
                                onClick={() => setPlanInfo(p => ({ ...p, frequency: ft }))}
                                style={{
                                  padding: "9px 12px",
                                  border: `1px solid ${planInfo.frequency === ft ? "#C0335A" : "#E0E0E0"}`,
                                  borderRadius: "8px",
                                  background: planInfo.frequency === ft ? "#FDE8EF" : "#fff",
                                  color: planInfo.frequency === ft ? "#C0335A" : "#374151",
                                  fontWeight: planInfo.frequency === ft ? "600" : "400",
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  transition: "all 0.15s",
                                }}
                              >
                                {ft}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Start Date + Time Slot */}
                        <Row className="g-3" style={{ marginBottom: "16px" }}>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label style={labelStyle}>
                                Start Date <span style={{ color: "red" }}>*</span>
                                <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.startDate} />
                              </Form.Label>
                              <input
                                type="date"
                                value={planInfo.startDate}
                                min={new Date().toISOString().split("T")[0]}
                                onChange={e => {
                                  setPlanInfo(p => ({ ...p, startDate: e.target.value }));
                                  setPlanInfoErrors(err => ({ ...err, startDate: undefined }));
                                }}
                                className="purchase-input"
                                style={{ width: "100%", height: "40px", border: `1px solid ${planInfoErrors.startDate ? "#dc3545" : "#E9ECEF"}`, borderRadius: "6px", padding: "0 12px", fontSize: "14px" }}
                              />
                              {planInfoErrors.startDate && (
                                <div className="invalid-feedback d-block" style={{ fontSize: "12px" }}>
                                  {planInfoErrors.startDate}
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label style={labelStyle}>
                                Preferred Time Slot <span style={{ color: "red" }}>*</span>
                                <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.preferredTime} />
                              </Form.Label>
                              <Form.Select
                                value={planInfo.timeSlot}
                                onChange={e => {
                                  setPlanInfo(p => ({ ...p, timeSlot: e.target.value }));
                                  setPlanInfoErrors(err => ({ ...err, timeSlot: undefined }));
                                }}
                                isInvalid={!!planInfoErrors.timeSlot}
                                style={{ height: "40px" }}
                                className="purchase-input"
                              >
                                <option value="">-- Select Time --</option>
                                {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                              </Form.Select>
                              {planInfoErrors.timeSlot && (
                                <div className="invalid-feedback d-block" style={{ fontSize: "12px" }}>
                                  {planInfoErrors.timeSlot}
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>

                        {/* Day(s) of the week */}
                        <Form.Group style={{ marginBottom: "16px" }}>
                          <Form.Label style={labelStyle}>
                            Day(s) of the Week <span style={{ color: "red" }}>*</span>
                            <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.daysOfWeek} />
                          </Form.Label>
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            {DAYS.map(day => (
                              <button
                                key={day}
                                type="button"
                                onClick={() => {
                                  setPlanInfo(p => ({
                                    ...p,
                                    days: p.days.includes(day)
                                      ? p.days.filter(d => d !== day)
                                      : [...p.days, day],
                                  }));
                                  setPlanInfoErrors(err => ({ ...err, days: undefined }));
                                }}
                                style={{
                                  padding: "7px 12px",
                                  border: `1px solid ${planInfo.days.includes(day) ? "#C0335A" : "#E0E0E0"}`,
                                  borderRadius: "8px",
                                  background: planInfo.days.includes(day) ? "#FDE8EF" : "#fff",
                                  color: planInfo.days.includes(day) ? "#C0335A" : "#374151",
                                  fontWeight: planInfo.days.includes(day) ? "600" : "400",
                                  fontSize: "13px",
                                  cursor: "pointer",
                                  transition: "all 0.15s",
                                }}
                              >
                                {day}
                              </button>
                            ))}
                          </div>
                          {planInfoErrors.days && (
                            <div className="invalid-feedback d-block" style={{ fontSize: "12px", marginTop: "4px" }}>
                              {planInfoErrors.days}
                            </div>
                          )}
                          {planInfo.frequency === "Weekly" && planInfo.days.length > 0 && (
                            <div style={{ marginTop: "8px", padding: "10px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "8px", fontSize: "13px", color: "#1D4ED8" }}>
                              Tip: If a daily subscription is needed, select all days to schedule it every day.
                            </div>
                          )}
                          {["Monthly", "Quarterly", "Half Yearly"].includes(planInfo.frequency) && (() => {
                            const msg = deriveScheduleMessage(planInfo.frequency, planInfo.startDate, planInfo.days);
                            return msg ? (
                              <div style={{ marginTop: "8px", padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "8px", fontSize: "13px", color: "#15803D" }}>
                                {msg}
                              </div>
                            ) : null;
                          })()}
                        </Form.Group>

                        {/* Crew Assignment */}
                        <Form.Group>
                          <Form.Label style={labelStyle}>
                            {isClinicService ? "Doctor Assignment" : "Crew Assignment"}
                            <span style={{ fontWeight: "400", color: "#9CA3AF", marginLeft: "6px" }}>(optional)</span>
                            <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.crewAssignment} />
                          </Form.Label>
                          <Form.Select
                            value={planInfo.crewId || ""}
                            onChange={e => setPlanInfo(p => ({ ...p, crewId: e.target.value || null }))}
                            disabled={crewLoading}
                            style={{ height: "40px" }}
                            className="purchase-input"
                          >
                            <option value="">{crewLoading ? (isClinicService ? "Loading doctors…" : "Loading crew…") : (isClinicService ? "Select doctor (optional)" : "Select crew member (optional)")}</option>
                            {crewList.map(c => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </>
                    )}
                  </div>
                )}

                {/* ── Full Address ── */}
                <div style={{ marginBottom: "24px" }}>
                  <h6 style={sectionHeadingStyle}>Full Address</h6>

                  <Row className="g-3">
                    {/* Address with Google Autocomplete */}
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>
                          Address <span style={{ color: "red" }}>*</span>
                          <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.address} />
                        </Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ flex: 1 }}>
                            {isLoaded && (
                              <Autocomplete
                                onLoad={onLoad}
                                onPlaceChanged={() => onPlaceChanged(setFieldValue)}
                                options={{
                                  componentRestrictions: {
                                    country: currency === "INR" ? "in" : "ae",
                                  },
                                }}
                              >
                                <Form.Control
                                  type="text"
                                  name="location"
                                  placeholder="Search address"
                                  value={values.location}
                                  onChange={handleChange}
                                  className="form-input"
                                />
                              </Autocomplete>
                            )}
                          </div>
                          {values.location && (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => {
                                setFieldValue("location", "");
                                setFieldValue("area", "");
                                setFieldValue("state", "");
                                setFieldValue("lat", "");
                                setFieldValue("lng", "");
                                setLocationError("");
                              }}
                            >
                              ✖
                            </button>
                          )}
                        </div>
                        {locationError && (
                          <div
                            className="invalid-feedback d-block"
                            style={{ fontSize: "12px" }}
                          >
                            {locationError}
                          </div>
                        )}
                        {!locationError && touched.location && errors.location && (
                          <div
                            className="invalid-feedback d-block"
                            style={{ fontSize: "12px" }}
                          >
                            {errors.location}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* House / Flat / Building No */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>
                          House / Flat / Building No{" "}
                          <span style={{ color: "red" }}>*</span>
                          <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.houseNo} />
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="house_no"
                          placeholder="Enter house / flat / building no"
                          value={values.house_no}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.house_no && errors.house_no}
                          className="purchase-input"
                          style={{ height: "40px" }}
                        />
                        {touched.house_no && errors.house_no && (
                          <div
                            className="invalid-feedback d-block"
                            style={{ fontSize: "12px" }}
                          >
                            {errors.house_no}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Area */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>
                          Area <span style={{ color: "red" }}>*</span>
                          <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.area} />
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="area"
                          placeholder="Enter area details..."
                          value={values.area}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.area && errors.area}
                          className="purchase-input"
                          style={{ height: "40px" }}
                        />
                        {touched.area && errors.area && (
                          <div
                            className="invalid-feedback d-block"
                            style={{ fontSize: "12px" }}
                          >
                            {errors.area}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* State / Emirate */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>
                          {currency === "INR" ? "State" : "Emirate"}{" "}
                          <span style={{ color: "red" }}>*</span>
                          <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.state} />
                        </Form.Label>
                        <Form.Select
                          name="state"
                          value={values.state}
                          onChange={(e) => setFieldValue("state", e.target.value)}
                          onBlur={handleBlur}
                          isInvalid={touched.state && !!errors.state}
                          style={{ height: "40px" }}
                          className="purchase-input"
                        >
                          <option value="">
                            {currency === "INR" ? "Select State" : "Select Emirate"}
                          </option>
                          {states.map((s) => (
                            <option key={s.name} value={s.name}>{s.name}</option>
                          ))}
                        </Form.Select>
                        {touched.state && errors.state && (
                          <div
                            className="invalid-feedback d-block"
                            style={{ fontSize: "12px" }}
                          >
                            {errors.state}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Landmark */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>Landmark <FieldHelpTooltip {...HELP_CONTENT.bookings.landmark} /></Form.Label>
                        <Form.Control
                          type="text"
                          name="landmark"
                          placeholder="Enter landmark details..."
                          value={values.landmark}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className="purchase-input"
                          style={{ height: "40px" }}
                        />
                      </Form.Group>
                    </Col>

                    {/* Country */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>Country <FieldHelpTooltip {...HELP_CONTENT.purchaseSlider.country} /></Form.Label>
                        <Form.Select
                          name="country"
                          value={values.country}
                          onChange={(e) => setFieldValue("country", e.target.value)}
                          onBlur={handleBlur}
                          style={{ height: "40px" }}
                          className="purchase-input"
                        >
                          <option value="">Select Country</option>
                          {countries.map((c) => (
                            <option key={c.country_id} value={c.country_id}>{c.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {/* Lat / Lng */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label style={labelStyle}>
                          Longitude &amp; Latitude
                          <FieldHelpTooltip {...HELP_CONTENT.bookings.latLng} />
                        </Form.Label>
                        <Row className="g-2">
                          <Col>
                            <Form.Control
                              type="text"
                              name="lat"
                              placeholder="Latitude"
                              value={values.lat}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.lat && errors.lat}
                              className="purchase-input"
                              style={{ height: "40px", fontSize: "14px" }}
                            />
                          </Col>
                          <Col>
                            <Form.Control
                              type="text"
                              name="lng"
                              placeholder="Longitude"
                              value={values.lng}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.lng && errors.lng}
                              className="purchase-input"
                              style={{ height: "40px", fontSize: "14px" }}
                            />
                          </Col>
                        </Row>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
                </>
                )}

                {/* ── Review & Pay (payment step) ── */}
                {step === "payment" && (
                  <div style={{ marginBottom: "24px" }}>
                    <h6 style={sectionHeadingStyle}>Review &amp; Pay</h6>

                    {isRecurringPlan ? (
                      <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "16px", marginBottom: "18px" }}>
                        {proratePreviewLoading ? (
                          <div style={{ fontSize: "13px", color: "#6B7280" }}>Calculating price…</div>
                        ) : proratePreview ? (
                          <>
                            <PriceRow label="Monthly Recurring Amount" value={`${currency} ${Number(plan?.recurringAmount || 0).toFixed(2)}`} />
                            {proratePreview.total_scheduled_classes != null && (
                              <>
                                <PriceRow label="Scheduled classes this month" value={proratePreview.total_scheduled_classes} />
                                <PriceRow label="Classes already passed" value={proratePreview.missed_classes} />
                                <PriceRow label="Per-class cost" value={`${currency} ${Number(proratePreview.per_class_cost).toFixed(2)}`} />
                              </>
                            )}
                            <div style={{ borderTop: "1px solid #E5E7EB", margin: "10px 0" }} />
                            <PriceRow
                              label={proratePreview.is_prorated ? "Due today (pro-rated)" : "Due today"}
                              value={`${currency} ${Number(proratePreview.purchase_price).toFixed(2)}`}
                              bold
                            />
                            <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "8px" }}>
                              Then {currency} {Number(plan?.recurringAmount || 0).toFixed(2)}/month, due on the {plan?.recurringDueDay} of every month
                            </div>
                          </>
                        ) : (
                          <div style={{ fontSize: "13px", color: "#DC2626" }}>Could not calculate the price for this plan.</div>
                        )}
                      </div>
                    ) : (
                      <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "10px", padding: "16px", marginBottom: "18px" }}>
                        <PriceRow label="Plan Price (one-time)" value={`${currency} ${Number(plan?.pricing || 0).toFixed(2)}`} bold />
                      </div>
                    )}

                    <Form.Label style={labelStyle}>Payment Method</Form.Label>
                    <PaymentMethodSelector value={selectedPayment} onChange={setSelectedPayment} />

                    {selectedPayment === "cash" && paymentStatusOptions.length > 0 && (
                      <div style={{ marginTop: "14px" }}>
                        <Form.Label style={labelStyle}>Payment Status</Form.Label>
                        <Form.Select
                          value={cashPaymentStatus ?? ""}
                          onChange={e => setCashPaymentStatus(Number(e.target.value))}
                          style={{ height: "40px" }}
                          className="purchase-input"
                        >
                          {paymentStatusOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </Form.Select>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => setStep("details")}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#6B7280",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: "pointer",
                        padding: 0,
                        marginTop: "16px",
                      }}
                    >
                      ← Back to details
                    </button>
                  </div>
                )}

                {/* Footer actions */}
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    justifyContent: "flex-end",
                    paddingTop: "8px",
                    borderTop: "1px solid #E5E7EB",
                  }}
                >
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      background: "#fff",
                      border: "1px solid #D1D5DB",
                      borderRadius: "9999px",
                      padding: "10px 24px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  {step === "details" ? (
                    <button
                      type="button"
                      onClick={() => handleContinue(validateForm, setTouched, values)}
                      style={{
                        background: "#C0335A",
                        border: "none",
                        borderRadius: "9999px",
                        padding: "10px 28px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#fff",
                        cursor: "pointer",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#a52b4e"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#C0335A"; }}
                    >
                      Continue to Payment
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={submitting || !selectedPayment}
                      style={{
                        background: submitting || !selectedPayment ? "#e89fb7" : "#C0335A",
                        border: "none",
                        borderRadius: "9999px",
                        padding: "10px 28px",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#fff",
                        cursor: submitting || !selectedPayment ? "not-allowed" : "pointer",
                        opacity: submitting ? 0.7 : 1,
                        transition: "background 0.2s",
                      }}
                    >
                      {submitting ? "Processing..." : selectedPayment ? "Confirm Purchase" : "Select a payment method"}
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

const PriceRow = ({ label, value, bold = false }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
    <span style={{ fontSize: bold ? "14px" : "13px", color: bold ? "#111827" : "#6B7280", fontWeight: bold ? "700" : "400" }}>{label}</span>
    <span style={{ fontSize: bold ? "14px" : "13px", color: bold ? "#111827" : "#374151", fontWeight: bold ? "700" : "600" }}>{value}</span>
  </div>
);

export default PurchaseSlider;
